import { useEffect, useState } from "react";
// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Authentication layout components
import CustomBasicLayout from "layouts/auth/components/CustomBasicLayout";

import AuthService from "services/auth-service";

// for the reset I should take from the url the token sent and the email
const PasswordReset = () => {
  const [token, setToken] = useState(null);
  const [email, setEmail] = useState(null);
  const [notification, setNotification] = useState(false);

  const [inputs, setInputs] = useState({
    password: "",
    password_confirmation: "",
  });

  const [errors, setErrors] = useState({
    passwordError: false,
    confirmationError: false,
    error: false,
    textError: "",
  });

  const changeHandler = (e) => {
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    // get the token and email sent in the url
    const queryParams = new URLSearchParams(window.location.search);
    setToken(queryParams.get("token"));
    setEmail(queryParams.get("email"));
  }, []);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (inputs.password.trim().length < 6) {
      setErrors({ ...errors, passwordError: true });
      return;
    }

    if (inputs.password_confirmation.trim() !== inputs.password.trim()) {
      setErrors({ ...errors, confirmationError: true });
      return;
    }

    const formData = {
      password: inputs.password,
      password_confirmation: inputs.password_confirmation,
      email: email,
      token: token,
    };

    const myData = {
      data: {
        type: "password-reset",
        attributes: { ...formData },
      },
    };

    try {
      const response = await AuthService.resetPassword(myData);

      setInputs({
        password: "",
        password_confirmation: "",
      });

      setErrors({
        passwordError: false,
        confirmationError: false,
        error: false,
        textError: "",
      });

      if (errors.passwordError === false && errors.confirmationError === false) {
        setNotification(true);
      }
    } catch (err) {
      if (err.hasOwnProperty("errors")) {
        setErrors({ ...errors, error: true, textError: err.errors.password[0] });
      }
      return null;
    }
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 3,
      backgroundColor: "#f8f9fa",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#465475" },
      "&.Mui-focused fieldset": { borderColor: "#465475", borderWidth: 2 },
    },
  };

  return (
    <CustomBasicLayout>
      <Card sx={{ maxWidth: 420, width: "100%", p: 4, borderRadius: 3 }}>
        {/* Logo/Icon */}
        <MDBox display="flex" flexDirection="column" alignItems="center" mb={3}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={80}
            height={80}
            borderRadius="50%"
            sx={{
              background: "linear-gradient(135deg, #5a6a94 0%, #465475 100%)",
              boxShadow: "0 4px 20px rgba(70, 84, 117, 0.3)",
              mb: 2,
            }}
          >
            <Icon sx={{ fontSize: 36, color: "white" }}>lock_reset</Icon>
          </MDBox>
          <MDTypography variant="h4" fontWeight="bold" color="dark">
            Reset Password
          </MDTypography>
          <MDTypography variant="body2" color="text" sx={{ mt: 0.5, textAlign: "center" }}>
            Enter your new password below
          </MDTypography>
        </MDBox>

        {/* Notification */}
        {notification && (
          <MDAlert color="info" sx={{ mb: 3 }}>
            <MDTypography variant="body2" color="white">
              Your password change was successful. Go back to
              <MDTypography
                component={Link}
                to="/login"
                variant="body2"
                fontWeight="medium"
                color="white"
              >
                &nbsp;login&nbsp;
              </MDTypography>
              to authenticate.
            </MDTypography>
          </MDAlert>
        )}

        {/* Form */}
        <MDBox component="form" onSubmit={submitHandler}>
          {/* Password */}
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              New Password
            </MDTypography>
            <TextField
              fullWidth
              type="password"
              name="password"
              placeholder="Enter new password"
              value={inputs.password}
              onChange={changeHandler}
              error={errors.passwordError}
              helperText={errors.passwordError ? "Password must be at least 6 characters" : ""}
              sx={inputStyles}
            />
          </MDBox>

          {/* Password Confirmation */}
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              Confirm Password
            </MDTypography>
            <TextField
              fullWidth
              type="password"
              name="password_confirmation"
              placeholder="Confirm new password"
              value={inputs.password_confirmation}
              onChange={changeHandler}
              error={errors.confirmationError}
              helperText={errors.confirmationError ? "Passwords must match" : ""}
              sx={inputStyles}
            />
          </MDBox>

          {/* Error Message */}
          {errors.error && (
            <MDBox mb={2} p={1.5} borderRadius={2} sx={{ backgroundColor: "#fee2e2" }}>
              <MDTypography variant="caption" color="error" fontWeight="medium">
                {errors.textError}
              </MDTypography>
            </MDBox>
          )}

          {/* Reset Button */}
          <MDButton
            variant="gradient"
            color="info"
            fullWidth
            size="large"
            type="submit"
            sx={{
              borderRadius: 3,
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            Reset Password
          </MDButton>

          {/* Sign In Link */}
          <MDBox mt={3} textAlign="center">
            <MDTypography variant="button" color="text">
              Remember your password?{" "}
              <MDTypography
                component={Link}
                to="/login"
                variant="button"
                color="info"
                fontWeight="medium"
              >
                Sign in
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </CustomBasicLayout>
  );
};

export default PasswordReset;